import React, {ReactNode} from 'react';
import {Property} from 'csstype';
import styled from 'styled-components';

interface ProgressCircleProps {
  progressColor?: Property.Color;
  baseColor?: Property.Color;
  progressWidth: number;
  radius: number;
  fraction: number;
  children?: ReactNode;
}

/**
 * Circular progress indicator, based on
 * https://css-tricks.com/building-progress-ring-quickly/
 *
 * @param progressColor the color of the progress indicator.
 * @param baseColor the color of the ring itself (i.e., the part representing future progress).
 * @param progressWidth the width of the progress indicator, in pixels.
 * @param radius the total radius (i.e., from center to the outside of the indicator) of the circle, in pixels.
 * @param fraction the fraction of progress in the range of [0, 1].
 * @param children react children.
 * @constructor
 */
// Should extend FunctionComponent<ProgressCircleProps>, but we need to use this in react 15, where that's not a thing.
export const ProgressCircle = ({
  progressColor = 'blue',
  baseColor,
  progressWidth,
  radius,
  fraction,
  children,
}: ProgressCircleProps) => {
  const diameter = radius * 2;
  // r = the radius of the circle to DRAW. The progress width is the width of the stroke. Divide that by two because
  // the stroke is centered on the line that defines the circle, so only half of it lies within (the other half is
  // outside!)
  const r = radius - progressWidth / 2;
  // ensure svg dimensions are large enough to contain the whole circle. It can get cut off otherwise.
  const circumference = 2 * Math.PI * r;
  const progress = circumference * (1 - fraction);
  // Draw up to two circles: first (optional) one in the baseColor. Second, a partial circle in the progressColor is
  // drawn on top of that one.
  // We use the viewBox to make this easy -- it's a diameter x diameter square, perfectly inscribing the circle.
  // The center is thus at 50% of the view box. Easy!
  return (
    <div
      className="progress-circle-container"
      style={{
        width: diameter,
        height: diameter,
      }}>
      <svg
        className="progress-circle-svg"
        viewBox={`0 0 ${diameter} ${diameter}`}
        width={`${diameter}px`}
        height={`${diameter}px`}>
        {baseColor && (
          <circle
            stroke={baseColor}
            strokeWidth={progressWidth}
            fill={'transparent'}
            r={r}
            cx={'50%'}
            cy={'50%'}
          />
        )}
        <RotatedCircle
          stroke={progressColor}
          strokeWidth={progressWidth}
          fill={'transparent'}
          r={r}
          cx={'50%'}
          cy={'50%'}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={progress}
        />
      </svg>
      {children}
    </div>
  );
};

// Need to transform the progress indicator circle to get the desired effect of rotating clockwise starting at 90
// degrees.
const RotatedCircle = styled.circle`
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
`;
